/**
 * <AdminRaids> — admin trigger + management page for the Raid Boss
 * system. Lives at /admin/raids.
 *
 * Features:
 *  - "Spawn raid" button per server (no choice of pal/passives — the
 *    system rolls those randomly to keep raids surprising).
 *  - Live list of active raids with End / Cancel actions.
 *  - Recent raid history with status pills.
 *
 * Auto-refreshes every 30s so the active list stays current without
 * the admin having to hit reload.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Swords, MapPin, X, Check, RefreshCw, AlertTriangle, Repeat, Clock, Activity, Users, ShieldAlert } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
// Both pickers were MOVED to their own files (2026-10-05) so
// /admin/raids/manual can import the same implementation. Do not
// reintroduce a local copy of either.
import {
  PassivePicker, CATEGORY_EMOJI, TIER_COLOR,
  PassiveRankIcon, usePassiveIconMap,
} from "@/components/admin/PassivePicker";
import { PalPicker, usePalPool } from "@/components/admin/PalPicker";

const REFRESH_MS = 30_000;

const STATUS_COLOR = {
  active:    "bg-amber-500/20 text-amber-300 border-amber-500/30",
  caught:    "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  expired:   "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  cancelled: "bg-rose-500/20 text-rose-300 border-rose-500/30",
};

export default function AdminRaids() {
  const [servers, setServers] = useState([]);
  const [history, setHistory] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [presence, setPresence] = useState(null);
  const [levels, setLevels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [spawning, setSpawning] = useState({}); // {serverName: bool}
  const toast = useToast();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, hRes, schedRes, presRes, lvlRes] = await Promise.all([
        api.get("/admin/raids/servers"),
        api.get("/admin/raids"),
        api.get("/admin/raids/schedule"),
        api.get("/admin/raids/presence"),
        api.get("/admin/raids/levels"),
      ]);
      setServers(sRes.data?.servers || []);
      setHistory(hRes.data?.raids || []);
      setSchedule(schedRes.data || null);
      setPresence(presRes.data || null);
      setLevels(lvlRes.data || null);
    } catch (e) {
      toast.error?.(apiError(e, "Failed to load raids"));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    // Kick the first fetch on the next microtask so the synchronous
    // setLoading(true) inside fetchAll doesn't trip
    // react-hooks/set-state-in-effect — we're still rendering the
    // current frame at this point.
    const id = setTimeout(fetchAll, 0);
    const t = setInterval(fetchAll, REFRESH_MS);
    return () => { clearTimeout(id); clearInterval(t); };
  }, [fetchAll]);

  const spawn = async (server, opts = {}) => {
    // opts: { pal?: string, level?: number, passives?: string[], isBoss?: boolean, isLucky?: boolean }
    const customBits = [];
    if (opts.pal) customBits.push(`pal=${opts.pal}`);
    if (opts.level) customBits.push(`Lv ${opts.level}`);
    if (opts.passives?.length) customBits.push(`passives: ${opts.passives.join(", ")}`);
    if (opts.isLucky) customBits.push("lucky");
    if (opts.isBoss === false) customBits.push("not-boss");
    const summary = customBits.length
      ? `custom raid (${customBits.join(", ")})`
      : "a random raid boss";
    if (!window.confirm(
      `Spawn ${summary} on "${server}"?\n\n` +
      "This will fire an RCON command immediately and post a Discord " +
      "notification to that server's chat-bridge webhook."
    )) return;
    setSpawning((s) => ({ ...s, [server]: true }));
    try {
      const params = new URLSearchParams({ server });
      if (opts.pal) params.set("pal", opts.pal);
      if (opts.level) params.set("level", String(opts.level));
      if (opts.passives?.length) params.set("passives", opts.passives.join(","));
      if (opts.isLucky != null) params.set("is_lucky", String(opts.isLucky));
      if (opts.isBoss != null) params.set("is_boss", String(opts.isBoss));
      const { data } = await api.post(`/admin/raids/spawn?${params.toString()}`);
      const tags = [];
      if (data.is_boss !== false) tags.push("Boss");
      if (data.is_lucky) tags.push("Lucky");
      toast.success?.(
        `Lv ${data.level} ${data.pal_name}${tags.length ? ` (${tags.join(" · ")})` : ""} spawned at ${data.location_name}`,
      );
      fetchAll();
    } catch (e) {
      toast.error?.(apiError(e, "Spawn failed"));
    } finally {
      setSpawning((s) => ({ ...s, [server]: false }));
    }
  };

  // Re-summon the EXACT pal/level/passives/location from the most
  // recent raid on this server. Use case: nobody was online when the
  // last raid fired and it expired — give them a second shot without
  // re-rolling the dice.
  const respawnLast = async (server) => {
    const last = history.find((r) => r.server === server);
    if (!last) {
      toast.error?.(`No prior raid found for "${server}"`);
      return;
    }
    if (!window.confirm(
      `Respawn the last raid on "${server}"?\n\n` +
      `Lv ${last.level} ${last.pal_name} (${last.category}) at ${last.location_name}\n` +
      `Passives: ${(last.passives || []).join(", ") || "—"}\n\n` +
      `This will fire RCON + post Discord just like a fresh spawn.`
    )) return;
    setSpawning((s) => ({ ...s, [server]: true }));
    try {
      const { data } = await api.post(
        `/admin/raids/respawn-last?server=${encodeURIComponent(server)}`,
      );
      toast.success?.(
        `Respawned Lv ${data.level} ${data.pal_name} at ${data.location_name}`,
      );
      fetchAll();
    } catch (e) {
      toast.error?.(apiError(e, "Respawn failed"));
    } finally {
      setSpawning((s) => ({ ...s, [server]: false }));
    }
  };

  const endRaid = async (raidId, status) => {
    let winner = "";
    if (status === "caught") {
      winner = window.prompt(
        "Who caught the raid boss? (their Discord/in-game username)",
      ) || "";
      if (!winner.trim()) return;
    } else if (!window.confirm(`Mark this raid as "${status}"?`)) {
      return;
    }
    try {
      await api.post(
        `/admin/raids/${raidId}/end?status=${status}` +
        (winner ? `&winner=${encodeURIComponent(winner.trim())}` : ""),
      );
      toast.success?.(`Raid marked ${status}`);
      fetchAll();
    } catch (e) {
      toast.error?.(apiError(e, "Failed to end raid"));
    }
  };

  const active = history.filter((r) => r.status === "active");
  const past = history.filter((r) => r.status !== "active");

  return (
    <div className="space-y-6" data-testid="admin-raids">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Swords size={18} className="text-rose-400" /> Raid Boss Spawner
          </h2>
          <p className="text-xs text-[var(--muted)] mt-1 max-w-2xl">
            Roll a random raid boss (perfect IVs, max souls, 4 category-locked
            passives, no IsBoss flag → catchable) at a random predator or
            fast-travel point. Fires the Discord webhook automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          className="btn btn-ghost text-xs flex items-center gap-1.5"
          disabled={loading}
          data-testid="admin-raids-refresh"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </header>

      {/* Auto-schedule status — Wed/Sat 7pm EST */}
      <ScheduleCard schedule={schedule} />

      {/* Player-presence raid trigger status */}
      <PresenceCard presence={presence} />

      {/* Weekly streak payouts */}
      <StreakRewardsCard />

      {/* Admin-editable raid boss level ranges (1-255) */}
      <RaidLevelsCard onSaved={setLevels} />

      {/* Admin-editable raid pal pool */}
      <RaidPoolCard />

      {/* PalDefender 1.9.x boss battle name, damage meter and ranked prizes */}

      {/* Spawn buttons per server */}
      <section className="grid sm:grid-cols-2 gap-3" data-testid="admin-raids-spawn-grid">
        {servers.length === 0 && (
          <div className="text-sm text-[var(--muted)] glass-card p-4">
            No servers found in sftp.yml. Add a server entry first.
          </div>
        )}
        {servers.map((s) => (
          <SpawnServerCard
            key={s.name}
            server={s}
            spawning={spawning[s.name]}
            hasHistory={history.some((r) => r.server === s.name)}
            levelRange={(levels?.resolved || []).find((r) => r.server === s.name)}
            onSpawn={spawn}
            onRespawnLast={respawnLast}
          />
        ))}
      </section>

      {/* Active raids — these need the "End" controls */}
      <section data-testid="admin-raids-active">
        <h3 className="text-sm uppercase tracking-wider text-[var(--muted)] mb-2 flex items-center gap-2">
          Active Raids
          {active.length > 0 && (
            <span className="text-xs bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded">
              {active.length}
            </span>
          )}
        </h3>
        {active.length === 0 ? (
          <div className="glass-card p-4 text-sm text-[var(--muted)]">
            No active raids. Spawn one above.
          </div>
        ) : (
          <ul className="space-y-2">
            {active.map((r) => <RaidRow key={r.id} raid={r} onEnd={endRaid} />)}
          </ul>
        )}
      </section>

      {/* Historical raids */}
      <section data-testid="admin-raids-history">
        <h3 className="text-sm uppercase tracking-wider text-[var(--muted)] mb-2">
          Recent History
        </h3>
        {past.length === 0 ? (
          <div className="glass-card p-4 text-sm text-[var(--muted)]">
            No past raids yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {past.slice(0, 20).map((r) => (
              <li
                key={r.id}
                className="glass-card p-3 flex items-center gap-3 text-sm"
                data-testid={`admin-raid-history-${r.id}`}
              >
                {r.pal_image && (
                  <img src={r.pal_image} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" loading="lazy" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="truncate">
                    <span className="font-bold">Lv {r.level} {r.pal_name}</span>
                    <span className="text-[var(--muted)]"> on {r.server} — {r.location_name}</span>
                  </div>
                  <div className="text-xs text-[var(--muted)]">
                    {CATEGORY_EMOJI[r.category]} {r.category} · {new Date(r.started_at).toLocaleString()}
                    {r.winner && <span> · 🏆 {r.winner}</span>}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${STATUS_COLOR[r.status] || ""}`}>
                  {r.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Per-server next-run card. Vanilla servers fire Wed+Sat 7pm ET,
 *  modded servers fire Mon+Fri 7pm ET. */
/**
 * <PresenceCard> — status of the player-presence raid trigger. One
 * row per server showing today's fired-raid count (X/5), a
 * cooldown-left pill, and the currently tracked player sessions
 * with their elapsed session time and whether they've been whispered
 * / triggered a raid yet.
 *
 * This card exists because raids now fire based on player presence
 * (see 2026-02-23 CHANGELOG). Without a per-server dashboard, the
 * operator has no way to see WHY a raid hasn't fired — cap hit,
 * cooldown, empty server, or a session that hasn't reached the
 * 5-minute threshold yet.
 */
function PresenceCard({ presence }) {
  if (!presence?.servers?.length) return null;

  const minutes = (s) => Math.floor(s / 60);
  const seconds = (s) => String(s % 60).padStart(2, "0");
  const fmtElapsed = (s) => `${minutes(s)}:${seconds(s)}`;

  return (
    <section
      className="glass-card p-4 flex items-start gap-3"
      data-testid="admin-raids-presence"
    >
      <Users size={18} className="text-emerald-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm font-bold">Player-Presence Raid Trigger</div>
          <span
            className={
              "text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border " +
              (presence.watcher_alive
                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                : "bg-rose-500/20 text-rose-300 border-rose-500/30")
            }
            data-testid="admin-raids-presence-watcher-status"
          >
            {presence.watcher_alive ? "LIVE" : "STOPPED"}
          </span>
        </div>
        <div className="text-xs text-[var(--muted)] mt-0.5">
          A raid fires when a player has been in-game ≥ 5 min. Max 5 raids/server/day, 20-min cooldown between fires.
        </div>
        <div className="mt-3 space-y-3">
          {presence.servers.map((s) => {
            const capHit = s.raids_today >= s.raids_max;
            return (
              <div
                key={s.server}
                className="border-t border-white/5 pt-2 first:border-t-0 first:pt-0"
                data-testid={`admin-raids-presence-${s.server.replace(/\s+/g, "-")}`}
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{s.server}</span>
                  <span
                    className={
                      "text-[11px] px-1.5 py-0.5 rounded font-semibold " +
                      (capHit
                        ? "bg-rose-500/20 text-rose-300"
                        : s.raids_today > 0
                        ? "bg-amber-500/20 text-amber-300"
                        : "bg-emerald-500/20 text-emerald-300")
                    }
                    data-testid={`admin-raids-presence-${s.server.replace(/\s+/g, "-")}-counter`}
                  >
                    {s.raids_today} / {s.raids_max} today
                  </span>
                  {s.cooldown_left > 0 && (
                    <span
                      className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-500/20 text-zinc-300 flex items-center gap-1"
                      data-testid={`admin-raids-presence-${s.server.replace(/\s+/g, "-")}-cooldown`}
                    >
                      <Clock size={10} /> {fmtElapsed(s.cooldown_left)} cooldown
                    </span>
                  )}
                </div>
                <div className="mt-1.5">
                  {s.sessions.length === 0 ? (
                    <div
                      className="text-xs text-[var(--muted)] italic"
                      data-testid={`admin-raids-presence-${s.server.replace(/\s+/g, "-")}-empty`}
                    >
                      No players online.
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {s.sessions.map((sess) => {
                        const ready = sess.seconds_online >= s.min_session_seconds;
                        return (
                          <li
                            key={sess.user_id}
                            className="flex items-center justify-between gap-2 text-xs"
                            data-testid={`admin-raids-presence-session-${sess.user_id}`}
                          >
                            <span className="truncate flex items-center gap-1.5">
                              <Users size={12} className="opacity-60" />
                              <span className="font-medium">{sess.name}</span>
                              {sess.raid_triggered && (
                                <ShieldAlert size={12} className="text-amber-400" />
                              )}
                            </span>
                            <span
                              className={
                                "px-1.5 py-0.5 rounded font-mono " +
                                (ready
                                  ? "bg-emerald-500/20 text-emerald-300"
                                  : sess.whispered
                                  ? "bg-amber-500/20 text-amber-300"
                                  : "bg-zinc-500/20 text-zinc-300")
                              }
                            >
                              {fmtElapsed(sess.seconds_online)}
                              {sess.whispered && !sess.raid_triggered && " · whispered"}
                              {sess.raid_triggered && " · triggered"}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}



function ScheduleCard({ schedule }) {
  // Tick a `nowTs` once per minute so the countdown decrements live
  // without re-fetching the whole AdminRaids page. Reading Date.now()
  // directly in render trips react-hooks/purity in React 19, so we
  // pin the timestamp into state and bump it.
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!schedule?.servers?.length) return null;
  const fmt = (iso) => new Date(iso).toLocaleString(undefined, {
    weekday: "long", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  });
  const countdown = (iso) => {
    const diffMs = new Date(iso).getTime() - nowTs;
    if (diffMs <= 0) return "now";
    const days = Math.floor(diffMs / 86_400_000);
    const hours = Math.floor((diffMs % 86_400_000) / 3_600_000);
    const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
    return days > 0 ? `${days}d ${hours}h ${minutes}m`
                    : hours > 0 ? `${hours}h ${minutes}m`
                                : `${minutes}m`;
  };
  return (
    <section
      className="glass-card p-4 flex items-start gap-3"
      data-testid="admin-raids-schedule"
    >
      <Clock size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm font-bold">Auto-Spawn Schedule</div>
          <HealthPill health={schedule.health} />
        </div>
        <div className="text-xs text-[var(--muted)] mt-0.5">
          Each server gets a random raid at 7:00 PM ET on its scheduled days.
        </div>
        <div className="mt-2 space-y-1.5">
          {schedule.servers.map((s) => (
            <div
              key={s.server}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm"
              data-testid={`admin-raids-schedule-${s.server.replace(/\s+/g, "-")}`}
            >
              <div className="min-w-0">
                <span className="font-bold truncate">{s.server}</span>
                <span className="text-[var(--muted)] text-xs">
                  {" "}· {s.weekday_labels.join(" + ")}
                  {s.modded && <span className="ml-1 text-amber-400">(modded)</span>}
                </span>
              </div>
              <div className="text-xs font-mono whitespace-nowrap">
                <span className="text-amber-300">{fmt(s.next_run)}</span>
                <span className="text-[var(--muted)]"> · in </span>
                <span className="font-bold">{countdown(s.next_run)}</span>
              </div>
            </div>
          ))}
        </div>
        {schedule.recent?.length > 0 && (
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer text-[var(--muted)] hover:text-white">
              Last {schedule.recent.length} scheduled fires
            </summary>
            <ul className="mt-1 space-y-0.5 font-mono">
              {schedule.recent.map((r, i) => (
                <li key={`${r.server}-${r.scheduled_date}-${i}`} className="text-[var(--muted)]">
                  {r.scheduled_date} · {r.server} · {r.raid_id ? "fired" : "claim-only"}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
}

/** One row in the Active Raids list with End controls. */
function RaidRow({ raid, onEnd }) {
  const byName = usePassiveIconMap();
  return (
    <li
      className="glass-card p-3 flex items-center gap-3 text-sm border-l-4 border-amber-500/60"
      data-testid={`admin-raid-active-${raid.id}`}
    >
      {raid.pal_image && (
        <img src={raid.pal_image} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" loading="lazy" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">
          Lv {raid.level} {raid.pal_name} {CATEGORY_EMOJI[raid.category]}
        </div>
        <div className="text-xs text-[var(--muted)] flex items-center gap-1 mt-0.5">
          <MapPin size={10} className="text-[var(--muted)]" />
          {raid.location_name} · {raid.server}
        </div>
        <div className="text-[10px] text-[var(--muted)] mt-0.5 flex flex-wrap gap-1">
          {(raid.passives || []).map((p) => (
            <span key={p}
              className={`px-1.5 py-0.5 rounded bg-white/5 font-mono flex items-center gap-1 ${TIER_COLOR[byName[p]?.tier] || ""}`}>
              <PassiveRankIcon tier={byName[p]?.tier} icon={byName[p]?.icon} size={11} />
              {p}
            </span>
          ))}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-1.5 flex-shrink-0">
        <button
          type="button"
          onClick={() => onEnd(raid.id, "caught")}
          className="btn btn-ghost text-xs flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
          data-testid={`admin-raid-${raid.id}-caught`}
        >
          <Check size={12} /> Caught
        </button>
        <button
          type="button"
          onClick={() => onEnd(raid.id, "expired")}
          className="btn btn-ghost text-xs flex items-center gap-1"
          data-testid={`admin-raid-${raid.id}-expired`}
        >
          Expired
        </button>
        <button
          type="button"
          onClick={() => onEnd(raid.id, "cancelled")}
          className="btn btn-ghost text-xs flex items-center gap-1 text-rose-400 hover:text-rose-300"
          data-testid={`admin-raid-${raid.id}-cancelled`}
        >
          <X size={12} /> Cancel
        </button>
      </div>
    </li>
  );
}


/**
 * <SpawnServerCard> — per-server raid trigger card with inline pal /
 * boss / lucky controls. Lives inside the "Quick Spawn" grid on the
 * Admin → Raids page.
 *
 * State is local so each server card remembers its last selection
 * independently — toggling "Lucky" on Pvpve doesn't affect Modded.
 *
 * Pool comes from GET /admin/raids/pool?server=… which is already
 * narrowed by server tier (vanilla servers skip tower bosses + modded
 * legendaries that PalDefender's vanilla PalID table rejects).
 */
function SpawnServerCard({ server, spawning, hasHistory, levelRange, onSpawn, onRespawnLast }) {
  const { pool, others } = usePalPool(server.name);
  const [pal, setPal] = useState("");      // empty = random
  const [level, setLevel] = useState("");  // empty = roll from configured range
  const [passives, setPassives] = useState([]); // empty = auto-roll from category
  const [isBoss, setIsBoss] = useState(true);
  const [isLucky, setIsLucky] = useState(false);

  const handleSpawn = () => {
    const lvl = parseInt(level, 10);
    onSpawn(server.name, {
      pal: pal || undefined,
      level: Number.isFinite(lvl) && lvl >= 1 && lvl <= 255 ? lvl : undefined,
      passives: passives.length ? passives : undefined,
      isBoss,
      isLucky,
    });
  };

  const slug = server.name.replace(/\s+/g, "-");
  const selected = [...pool, ...others].find((p) => p.name === pal);
  const safeCount = pool.filter((p) => p.safe !== false).length;
  const isExperimentalSelection = selected?.safe === false;
  const rangeLabel = levelRange
    ? levelRange.min === levelRange.max
      ? `Lv ${levelRange.min}`
      : `Lv ${levelRange.min}-${levelRange.max}`
    : "Lv —";

  return (
    <div
      className="glass-card p-4 flex flex-col gap-3"
      data-testid={`admin-raids-server-${server.name}`}
    >
      {/* Server header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-bold text-sm truncate">{server.name}</div>
          <div className="text-xs text-[var(--muted)] flex gap-2 mt-0.5 flex-wrap">
            <span>{server.modded ? "Modded" : "Vanilla"} · {rangeLabel}</span>
            <span className="opacity-60">·</span>
            <span>
              {safeCount} safe
              {pool.length > safeCount && (
                <span className="text-amber-400/80"> · {pool.length - safeCount} experimental</span>
              )}
            </span>
            {!server.has_webhook && (
              <span className="text-amber-400 flex items-center gap-1">
                <AlertTriangle size={10} /> no webhook
              </span>
            )}
          </div>
        </div>
        {selected?.image && (
          <img
            src={selected.image}
            alt={selected.name}
            className="w-10 h-10 rounded object-cover border border-white/10 flex-shrink-0"
            loading="lazy"
          />
        )}
      </div>

      {/* Inline spawn controls */}
      <div className="flex flex-wrap items-center gap-2">
        <PalPicker
          value={pal}
          onChange={setPal}
          pool={pool}
          others={others}
          slug={slug}
        />
        <input
          type="number"
          min={1}
          max={255}
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          placeholder={levelRange ? `${levelRange.min}-${levelRange.max}` : "Lv"}
          title="Level override (1-255). Leave blank to roll from the configured range."
          className="input text-xs" style={{ width: 74 }}
          data-testid={`admin-raids-level-${slug}`}
        />
        <label
          className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border cursor-pointer transition-colors ${
            isBoss
              ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
              : "border-white/10 bg-white/[0.03] text-[var(--muted)]"
          }`}
          title="Alpha boss — crown icon in-world, larger model"
        >
          <input
            type="checkbox"
            checked={isBoss}
            onChange={(e) => setIsBoss(e.target.checked)}
            className="sr-only"
            data-testid={`admin-raids-boss-${slug}`}
          />
          👑 Boss
        </label>
        <label
          className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border cursor-pointer transition-colors ${
            isLucky
              ? "border-yellow-400/40 bg-yellow-400/10 text-yellow-200"
              : "border-white/10 bg-white/[0.03] text-[var(--muted)]"
          }`}
          title="Lucky variant — sparkle visual, bonus drop multiplier"
        >
          <input
            type="checkbox"
            checked={isLucky}
            onChange={(e) => setIsLucky(e.target.checked)}
            className="sr-only"
            data-testid={`admin-raids-lucky-${slug}`}
          />
          ✨ Lucky
        </label>
        <PassivePicker selected={passives} onChange={setPassives} slug={slug} />
      </div>

      {/* Action buttons */}
      <div className="flex gap-1.5 flex-wrap">
        <button
          type="button"
          onClick={handleSpawn}
          disabled={spawning}
          className="btn btn-cta text-xs flex items-center gap-1.5 flex-1 justify-center min-w-[110px]"
          data-testid={`admin-raids-spawn-${slug}`}
        >
          {spawning ? (
            <><RefreshCw size={12} className="animate-spin" /> Spawning…</>
          ) : (
            <><Swords size={12} /> Spawn Raid</>
          )}
        </button>
        <button
          type="button"
          onClick={() => onRespawnLast(server.name)}
          disabled={spawning || !hasHistory}
          className="btn btn-ghost text-xs flex items-center gap-1.5"
          title="Re-summon the exact pal, level, passives & location from the last raid on this server"
          data-testid={`admin-raids-respawn-last-${slug}`}
        >
          <Repeat size={12} /> Respawn Last
        </button>
      </div>

      {isExperimentalSelection && (
        <div
          className="text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1 flex items-start gap-1.5"
          data-testid={`admin-raids-experimental-warning-${slug}`}
        >
          <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
          <span>
            <strong>{selected?.name}</strong> was previously gated as modded-only on this server.
            If PalDefender rejects it, the error toast will show the exact reason — try a different pal.
          </span>
        </div>
      )}
    </div>
  );
}




/**
 * <HealthPill> — colour-coded liveness indicator for the raid
 * scheduler, rendered inside <ScheduleCard>. Polled every 30s by the
 * parent's auto-refresh. The poll itself is the watchdog: the
 * `/admin/raids/schedule` endpoint revives a dead scheduler task and
 * force-fires a missed raid on every call.
 *
 * Status colours:
 *   ok    → green   (last tick < 2 min)
 *   stale → amber   (last tick 2-10 min — possible lag)
 *   dead  → red     (last tick > 10 min or never recorded)
 *
 * If the endpoint reports `revived: true` (we just restarted a dead
 * task) or `auto_fired > 0` (catch-up triggered a raid this poll),
 * the pill grows a one-line note so the admin sees the self-heal
 * happened.
 */
function HealthPill({ health }) {
  if (!health) return null;
  const { status, seconds_since_tick, task_running, revived, auto_fired } = health;
  const labels = {
    ok:    { dot: "bg-emerald-400", text: "text-emerald-300", border: "border-emerald-500/30", bg: "bg-emerald-500/10", label: "Alive" },
    stale: { dot: "bg-amber-400",   text: "text-amber-300",   border: "border-amber-500/30",   bg: "bg-amber-500/10",   label: "Stale" },
    dead:  { dot: "bg-rose-500",    text: "text-rose-300",    border: "border-rose-500/30",    bg: "bg-rose-500/10",    label: "Dead" },
  };
  const s = labels[status] || labels.dead;
  const ageStr =
    seconds_since_tick == null
      ? "never ticked"
      : seconds_since_tick < 90
      ? `tick ${seconds_since_tick}s ago`
      : seconds_since_tick < 3600
      ? `tick ${Math.round(seconds_since_tick / 60)}m ago`
      : `tick ${Math.round(seconds_since_tick / 3600)}h ago`;

  return (
    <div
      className={`flex flex-col items-end gap-0.5 text-[10px] uppercase tracking-wider`}
      data-testid="admin-raids-health"
    >
      <span
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border ${s.border} ${s.bg} ${s.text}`}
        title={
          task_running
            ? `Scheduler task is running — last heartbeat ${ageStr}`
            : "Scheduler task is NOT running. Endpoint will revive it on next poll."
        }
        data-testid={`admin-raids-health-${status}`}
      >
        <Activity size={11} />
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} aria-hidden />
        {s.label} · {ageStr}
      </span>
      {(revived || auto_fired > 0) && (
        <span className="text-[10px] text-amber-300 normal-case tracking-normal" data-testid="admin-raids-health-action">
          {revived && "↻ task revived  "}
          {auto_fired > 0 && `⚔ auto-fired ${auto_fired} raid${auto_fired === 1 ? "" : "s"}`}
        </span>
      )}
    </div>
  );
}

/* ---------- Weekly streak payouts ---------- */

function StreakRewardsCard() {
  const [cfg, setCfg] = useState(null);
  const [awards, setAwards] = useState([]);
  const [tiers, setTiers] = useState("");
  const [bonus, setBonus] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/raids/streak-config");
      setCfg(data.config);
      setAwards(data.awards || []);
      setTiers((data.config.top_coins || []).join(", "));
      setBonus(String(data.config.streak_bonus_per_week ?? 0));
    } catch (e) { toast.error(apiError(e)); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/raids/streak-config", patch);
      setCfg(data.config);
      toast.success("Streak rewards saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const settle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/raids/settle-week");
      toast.success(
        data.status === "already_settled"
          ? "That week was already paid out"
          : `Settled — ${data.awards.length} awards`,
      );
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!cfg) return null;

  return (
    <section className="glass-card p-4" data-testid="admin-streak-rewards">
      <div className="flex items-center gap-2 flex-wrap">
        <Repeat size={15} className="text-amber-300" />
        <h3 className="text-sm font-semibold">Weekly streak payouts</h3>
        <label className="text-xs flex items-center gap-1.5 ml-2">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => save({ enabled: e.target.checked })}
            data-testid="streak-enabled"
          />
          enabled
        </label>
        <label className="text-xs flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={!!cfg.announce}
            onChange={(e) => save({ announce: e.target.checked })}
            data-testid="streak-announce"
          />
          announce on Discord
        </label>
        <button
          type="button"
          onClick={settle}
          disabled={busy}
          className="btn btn-ghost text-xs ml-auto"
          data-testid="streak-settle-now"
        >
          Settle last week now
        </button>
      </div>
      <p className="text-xs text-[var(--muted)] mt-2">
        Paid automatically every Monday 00:00 UTC from the previous week's catch
        board. Unlinked winners still get a row here — they just can't be credited
        until they run <code>/link</code>.
      </p>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <div>
          <span className="label">Podium coins (rank 1, 2, 3…)</span>
          <div className="flex gap-2 mt-1">
            <input
              className="input flex-1"
              value={tiers}
              onChange={(e) => setTiers(e.target.value)}
              data-testid="streak-tiers"
            />
            <button
              type="button"
              className="btn btn-ghost text-xs"
              disabled={busy}
              onClick={() => save({
                top_coins: tiers.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !Number.isNaN(n)),
              })}
              data-testid="streak-tiers-save"
            >
              Save
            </button>
          </div>
        </div>
        <div>
          <span className="label">Bonus per streak week</span>
          <div className="flex gap-2 mt-1">
            <input
              type="number"
              className="input flex-1"
              value={bonus}
              onChange={(e) => setBonus(e.target.value)}
              data-testid="streak-bonus"
            />
            <button
              type="button"
              className="btn btn-ghost text-xs"
              disabled={busy}
              onClick={() => save({ streak_bonus_per_week: parseInt(bonus, 10) || 0 })}
              data-testid="streak-bonus-save"
            >
              Save
            </button>
          </div>
        </div>
      </div>
      {awards.length > 0 && (
        <div className="mt-3 divide-y divide-white/5" data-testid="admin-streak-awards">
          {awards.slice(0, 8).map((a) => (
            <div key={a.id} className="py-2 text-xs flex items-center gap-3">
              <span className="text-[var(--muted)] font-mono">{String(a.week_start).slice(0, 10)}</span>
              <span className="w-5 text-center">{a.rank}</span>
              <span className="flex-1 min-w-0 truncate">{a.player}</span>
              <span className="text-[var(--muted)] hidden sm:inline truncate">{a.server}</span>
              <span className="text-amber-300">+{(a.coins || 0).toLocaleString()}</span>
              <span className={a.paid ? "text-emerald-300" : "text-[var(--muted)]"}>
                {a.paid ? "paid" : "unlinked"}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------- Raid boss level ranges (admin-editable, 1-255) ----------
 * Tier defaults (vanilla / modded) plus optional per-server overrides.
 * Stored in economy_settings['raids.levels'] so the bot reads the same
 * values. Blank/removed override → the tier default applies.
 */
function RaidLevelsCard({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/raids/levels");
      setCfg(data);
      onSaved?.(data);
    } catch (e) {
      toast.error?.(apiError(e, "Failed to load raid levels"));
    }
  }, [toast, onSaved]);

  useEffect(() => { const id = setTimeout(load, 0); return () => clearTimeout(id); }, [load]);

  const setTier = (tier, key, value) =>
    setCfg((c) => ({ ...c, [tier]: { ...c[tier], [key]: value } }));

  const setServer = (name, key, value) =>
    setCfg((c) => ({
      ...c,
      servers: {
        ...c.servers,
        [name]: { ...(c.servers?.[name] || { min: "", max: "" }), [key]: value },
      },
    }));

  const setScaling = (key, value) =>
    setCfg((c) => ({ ...c, scaling: { ...(c.scaling || {}), [key]: value } }));

  const setStep = (idx, key, value) =>
    setCfg((c) => ({
      ...c,
      scaling: {
        ...c.scaling,
        steps: (c.scaling?.steps || []).map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
      },
    }));

  const clampRange = (r) => {
    let lo = parseInt(r?.min, 10);
    let hi = parseInt(r?.max, 10);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
    lo = Math.max(1, Math.min(255, lo));
    hi = Math.max(1, Math.min(255, hi));
    return lo <= hi ? { min: lo, max: hi } : { min: hi, max: lo };
  };

  const save = async () => {
    const payload = {
      vanilla: clampRange(cfg.vanilla),
      modded: clampRange(cfg.modded),
      servers: Object.fromEntries(
        Object.entries(cfg.servers || {}).map(([n, r]) => [n, clampRange(r)]),
      ),
      scaling: {
        enabled: !!cfg.scaling?.enabled,
        modded_fixed: Math.max(1, Math.min(255, parseInt(cfg.scaling?.modded_fixed, 10) || 255)),
        steps: (cfg.scaling?.steps || [])
          .map((s) => ({
            players: Math.max(1, Math.min(100, parseInt(s.players, 10) || 1)),
            level: Math.max(1, Math.min(255, parseInt(s.level, 10) || 65)),
          }))
          .sort((a, b) => a.players - b.players),
      },
    };
    if (!payload.vanilla || !payload.modded) {
      toast.error?.("Tier ranges need a min and a max between 1 and 255");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/admin/raids/levels", payload);
      setCfg(data);
      onSaved?.(data);
      toast.success?.("Raid levels saved");
    } catch (e) {
      toast.error?.(apiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset raid level ranges to the built-in defaults (80 / 80-100)?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/raids/levels/reset");
      setCfg(data);
      onSaved?.(data);
      toast.success?.("Raid levels reset");
    } catch (e) {
      toast.error?.(apiError(e, "Reset failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;

  return (
    <section className="glass-card p-4" data-testid="admin-raid-levels">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Swords size={14} className="text-rose-400" /> Raid levels
        </h3>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={reset}
            disabled={busy}
            className="btn btn-ghost text-xs"
            data-testid="raid-levels-reset"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="btn btn-cta text-xs"
            data-testid="raid-levels-save"
          >
            {busy ? "Saving…" : "Save levels"}
          </button>
        </div>
      </div>
      <p className="text-xs text-[var(--muted)] mt-1">
        Any level from 1 to 255. Random spawns and the scheduler roll inside these
        ranges; a per-server override beats its tier default. Set min = max for a
        fixed level.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <RangeRow
          label="Vanilla default"
          range={cfg.vanilla}
          onChange={(k, v) => setTier("vanilla", k, v)}
          testid="raid-levels-vanilla"
        />
        <RangeRow
          label="Modded default"
          range={cfg.modded}
          onChange={(k, v) => setTier("modded", k, v)}
          testid="raid-levels-modded"
        />
      </div>

      <h4 className="text-xs uppercase tracking-wider text-[var(--muted)] mt-4 mb-2">
        Per-server overrides
      </h4>
      <div className="space-y-2">
        {(cfg.resolved || []).map((r) => {
          const override = cfg.servers?.[r.server];
          return (
            <div
              key={r.server}
              className="flex items-center gap-2 flex-wrap text-xs"
              data-testid={`raid-levels-server-${r.server.replace(/\s+/g, "-")}`}
            >
              <span className="flex-1 min-w-[140px] truncate">
                {r.server}
                <span className="text-[var(--muted)]">
                  {" "}· {r.modded ? "modded" : "vanilla"}
                  {!override && ` · using ${r.min}-${r.max}`}
                </span>
              </span>
              {override ? (
                <>
                  <input
                    type="number" min={1} max={255}
                    className="input text-xs" style={{ width: 70 }}
                    value={override.min ?? ""}
                    onChange={(e) => setServer(r.server, "min", e.target.value)}
                    data-testid={`raid-levels-min-${r.server.replace(/\s+/g, "-")}`}
                  />
                  <span className="text-[var(--muted)]">–</span>
                  <input
                    type="number" min={1} max={255}
                    className="input text-xs" style={{ width: 70 }}
                    value={override.max ?? ""}
                    onChange={(e) => setServer(r.server, "max", e.target.value)}
                    data-testid={`raid-levels-max-${r.server.replace(/\s+/g, "-")}`}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost text-xs"
                    onClick={() =>
                      setCfg((c) => {
                        const next = { ...(c.servers || {}) };
                        delete next[r.server];
                        return { ...c, servers: next };
                      })
                    }
                    data-testid={`raid-levels-clear-${r.server.replace(/\s+/g, "-")}`}
                  >
                    <X size={11} />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost text-xs"
                  onClick={() =>
                    setCfg((c) => ({
                      ...c,
                      servers: { ...(c.servers || {}), [r.server]: { min: r.min, max: r.max } },
                    }))
                  }
                  data-testid={`raid-levels-override-${r.server.replace(/\s+/g, "-")}`}
                >
                  Override
                </button>
              )}
            </div>
          );
        })}
      </div>
      <h4 className="text-xs uppercase tracking-wider text-[var(--muted)] mt-4 mb-2">
        Automatic raid scaling
      </h4>
      <div className="space-y-2" data-testid="raid-levels-scaling">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={!!cfg.scaling?.enabled}
            onChange={(e) => setScaling("enabled", e.target.checked)}
            data-testid="raid-levels-scaling-enabled"
          />
          Scale scheduled &amp; auto raids by how many players are online
        </label>
        <p className="text-[11px] text-[var(--muted)] leading-snug">
          Applies to the scheduler, the player-presence trigger and treasure
          raids only — manual spawns above always use the ranges/level box.
          Modded servers skip the ladder and use a fixed level.
        </p>

        <div className="flex items-center gap-2 text-xs">
          <span className="flex-1">Modded servers — fixed level</span>
          <input
            type="number" min={1} max={255}
            className="input text-xs" style={{ width: 70 }}
            value={cfg.scaling?.modded_fixed ?? ""}
            onChange={(e) => setScaling("modded_fixed", e.target.value)}
            data-testid="raid-levels-scaling-modded"
          />
        </div>

        <div className="space-y-1.5">
          {(cfg.scaling?.steps || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs" data-testid={`raid-levels-step-${i}`}>
              <span className="text-[var(--muted)]">
                {i === (cfg.scaling.steps.length - 1) ? "at" : "at"}
              </span>
              <input
                type="number" min={1} max={100}
                className="input text-xs" style={{ width: 62 }}
                value={s.players}
                onChange={(e) => setStep(i, "players", e.target.value)}
                data-testid={`raid-levels-step-players-${i}`}
              />
              <span className="text-[var(--muted)]">
                player{Number(s.players) === 1 ? "" : "s"}
                {i === (cfg.scaling.steps.length - 1) ? " or more →" : " →"} Lv
              </span>
              <input
                type="number" min={1} max={255}
                className="input text-xs" style={{ width: 70 }}
                value={s.level}
                onChange={(e) => setStep(i, "level", e.target.value)}
                data-testid={`raid-levels-step-level-${i}`}
              />
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={() => setCfg((c) => ({
                  ...c,
                  scaling: { ...c.scaling, steps: c.scaling.steps.filter((_, j) => j !== i) },
                }))}
                data-testid={`raid-levels-step-remove-${i}`}
              >
                <X size={11} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-ghost text-xs"
            onClick={() => setCfg((c) => {
              const steps = c.scaling?.steps || [];
              const last = steps[steps.length - 1] || { players: 0, level: 65 };
              return {
                ...c,
                scaling: {
                  ...c.scaling,
                  steps: [...steps, { players: Number(last.players) + 1, level: last.level }],
                },
              };
            })}
            data-testid="raid-levels-step-add"
          >
            + Add step
          </button>
        </div>
      </div>
    </section>
  );
}

function RangeRow({ label, range, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testid}>
      <span className="flex-1">{label}</span>
      <input
        type="number" min={1} max={255}
        className="input text-xs" style={{ width: 70 }}
        value={range?.min ?? ""}
        onChange={(e) => onChange("min", e.target.value)}
        data-testid={`${testid}-min`}
      />
      <span className="text-[var(--muted)]">–</span>
      <input
        type="number" min={1} max={255}
        className="input text-xs" style={{ width: 70 }}
        value={range?.max ?? ""}
        onChange={(e) => onChange("max", e.target.value)}
        data-testid={`${testid}-max`}
      />
    </div>
  );
}


/* ---------- Raid pal pool (admin-editable) ----------
 * One shared pool across both servers, equal chance, add/remove/enable.
 * Stored as a single JSON row in economy_settings — no schema change.
 * Order matters: it drives the spawn dropdown, so we never sort it. */

function RaidPoolCard() {
  const [entries, setEntries] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [source, setSource] = useState("defaults");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/raids/pool-config");
      setEntries(data.entries || []);
      setCandidates(data.candidates || []);
      setSource(data.source);
      setDirty(false);
    } catch (e) { toast.error(apiError(e)); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const enabledCount = (entries || []).filter((e) => e.enabled).length;
  const raidsOff = entries !== null && enabledCount === 0;

  const toggle = (name) => {
    setEntries((prev) => prev.map((e) =>
      e.name === name ? { ...e, enabled: !e.enabled } : e));
    setDirty(true);
  };

  const remove = (name) => {
    setEntries((prev) => prev.filter((e) => e.name !== name));
    setDirty(true);
  };

  const add = () => {
    if (!pick) return;
    if ((entries || []).some((e) => e.name.toLowerCase() === pick.toLowerCase())) {
      toast.info?.(`${pick} is already in the pool.`);
      return;
    }
    setEntries((prev) => [...prev, { name: pick, enabled: true }]);
    setPick("");
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { entries: entries.map((e) => ({ name: e.name, enabled: !!e.enabled })) };
      const { data } = await api.put("/admin/raids/pool-config", payload);
      setSource(data.source);
      setDirty(false);
      const warned = Object.keys(data.warnings || {});
      toast.success(
        `Raid pool saved — ${data.active_count} pal${data.active_count === 1 ? "" : "s"} in rotation`,
      );
      if (warned.length) toast.info?.(`Cosmetic gaps on: ${warned.join(", ")}`);
      load();
    } catch (e) {
      const rejected = e?.response?.data?.detail?.rejected;
      if (Array.isArray(rejected)) {
        rejected.forEach((r) => toast.error(`${r.name}: ${r.reason}`));
      } else {
        toast.error(apiError(e));
      }
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.post("/admin/raids/pool-config/reset");
      toast.success("Raid pool reset to the 17 built-in pals");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (entries === null) return null;

  return (
    <section className="glass-card p-4" data-testid="admin-raid-pool">
      <div className="flex items-center gap-2 flex-wrap">
        <Swords size={15} className="text-rose-400" />
        <h3 className="text-sm font-semibold">Raid pal pool</h3>
        <span className="text-xs text-[var(--muted)]">
          {enabledCount}/{entries.length} in rotation
          {source === "defaults" && " · built-in defaults"}
        </span>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={busy}
            className="btn btn-ghost text-xs"
            data-testid="raid-pool-reset"
          >
            Reset to defaults
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy || !dirty}
            className="btn text-xs"
            data-testid="raid-pool-save"
          >
            {busy ? "Saving…" : dirty ? "Save pool" : "Saved"}
          </button>
        </div>
      </div>

      <p className="text-xs text-[var(--muted)] mt-2">
        One shared pool for every server, equal chance per pal — these stay pinned
        at the top of each server's spawn dropdown. Disabling a pal
        removes it from the random roll <em>and</em> from the manual spawn
        dropdown.
      </p>

      {raidsOff && (
        <div
          className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
          data-testid="raid-pool-off-banner"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            <strong>Raids are off.</strong> Every pal is disabled, so the
            scheduler and the presence trigger will stay quiet. Enable at least
            one pal to start raids again.
          </span>
        </div>
      )}

      <div className="flex gap-2 mt-3">
        <select
          className="input flex-1"
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          data-testid="raid-pool-picker"
        >
          <option value="">Add a pal…</option>
          {candidates
            .filter((c) => !entries.some((e) => e.name === c))
            .map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          type="button"
          onClick={add}
          disabled={!pick || busy}
          className="btn btn-ghost text-xs"
          data-testid="raid-pool-add"
        >
          Add
        </button>
      </div>

      <ul className="mt-3 divide-y divide-white/5" data-testid="raid-pool-list">
        {entries.map((e) => (
          <li
            key={e.name}
            className="py-2 flex items-center gap-2 text-sm"
            data-testid={`raid-pool-row-${e.name}`}
          >
            <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
              <input
                type="checkbox"
                checked={!!e.enabled}
                onChange={() => toggle(e.name)}
                data-testid={`raid-pool-toggle-${e.name}`}
              />
              <span className={e.enabled ? "" : "text-[var(--muted)] line-through"}>
                {e.name}
              </span>
              {e.valid === false && (
                <span
                  className="text-xs text-rose-300 flex items-center gap-1"
                  title={e.reason || "Cannot be spawned"}
                  data-testid={`raid-pool-invalid-${e.name}`}
                >
                  <AlertTriangle size={11} /> can't spawn
                </span>
              )}
              {e.valid !== false && e.warnings?.length > 0 && (
                <span
                  className="text-xs text-amber-300/80"
                  title={e.warnings.join(" ")}
                  data-testid={`raid-pool-warn-${e.name}`}
                >
                  ⚠
                </span>
              )}
            </label>
            <code className="text-[11px] text-[var(--muted)] hidden sm:inline">
              {e.spawn_id}
            </code>
            <button
              type="button"
              onClick={() => remove(e.name)}
              className="text-[var(--muted)] hover:text-rose-300 transition-colors"
              title={`Remove ${e.name}`}
              data-testid={`raid-pool-remove-${e.name}`}
            >
              <X size={13} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
